import { get } from 'lodash';
import getColour from '@lendi-ui/commons/colours';

import {
  ACCOUNT_BSB_QUESTIONS,
  ASSET_TYPE,
  ENTITY_TYPE,
  FIELD_PATHS,
  LIABILITY_QUESTIONS,
  LIABILITY_TYPE,
  PII_FIELD_MASKING_CLASS,
} from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import {
  Asset,
  Liability,
  LiabilityContractsMap,
  Owner,
  PostProductSelectContract,
  StoreApplicant,
} from '@funnels/types';
import {
  Accordion,
  ActionButtonsWrapper,
  AssetAccountBSBCustomerView,
  AssetAccountNumberCustomerView,
  Body,
  Container,
  ContentWrapper,
  CurrentInterestRateFunnelView,
  CurrentLenderFunnelView,
  EstimatedValueFunnelView,
  FinancialInstitutionFunnelView,
  FunnelGrid,
  FunnelGridUnit,
  Heading,
  HeadingWrapper,
  LiabilityAccountBSBCustomerView,
  LiabilityAccountNumberCustomerView,
  LiabilityCreditLimitFunnelView,
  NextArrowAction,
  OutstandingBalanceFunnelView,
} from '@funnels/ui-components';
import {
  getApplicantsFullnameFromOwners,
  getFieldValueLabel,
  getIndexFromId,
  getQuestionDisplayFields,
} from '@funnels/utils';

interface AccordionHeaderProps {
  header: string;
  subHeader: string;
}

const AccordionHeader = ({ header, subHeader }: AccordionHeaderProps) => (
  <>
    <Heading size="xs">{header}</Heading>
    <Body size="xs" color={getColour('text', 'subtle')} className={PII_FIELD_MASKING_CLASS}>
      {subHeader}
    </Body>
  </>
);

interface AccountDetailsProps {
  isFromSummary?: boolean;
}

export const AccountDetails = (props: AccountDetailsProps) => {
  const [formValues] = useFormValues();
  const { isFromSummary } = props;
  const liabilities: Liability[] = get(formValues, FIELD_PATHS.LIABILITIES(), []);
  const assets: Asset[] = get(formValues, FIELD_PATHS.ASSETS(), []);
  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS());
  const ppsContracts: PostProductSelectContract[] = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), []);

  const assetsAccountDetailsContracts: PostProductSelectContract[] = [];
  const liabilityContracts: LiabilityContractsMap = {};

  ppsContracts.forEach((entity) => {
    if (
      ![ENTITY_TYPE.ASSET_ACCOUNT, ENTITY_TYPE.LIABILITY_ACCOUNT, ENTITY_TYPE.LIABILITY].find(
        (t) => t === entity.entityType
      )
    )
      return; //early quit
    //ASSET_ACCOUNT
    if (entity.entityType === ENTITY_TYPE.ASSET_ACCOUNT) {
      assetsAccountDetailsContracts.push(entity);
      return;
    }
    // LIABILITY_ACCOUNT || LIABILITY
    const liabilityId = entity.entityId;
    if (!liabilityId) return; //early quit
    if (!liabilityContracts[liabilityId]) {
      liabilityContracts[liabilityId] = {}; // initialization
    }
    if (entity.entityType === ENTITY_TYPE.LIABILITY) liabilityContracts[liabilityId].liabilityContract = entity;
    if (entity.entityType === ENTITY_TYPE.LIABILITY_ACCOUNT)
      liabilityContracts[liabilityId].liabilityAccountContract = entity;
  });

  const heading = 'Provide your account details';

  return (
    <Container>
      <HeadingWrapper>
        <Heading size="md">{heading}</Heading>
      </HeadingWrapper>
      <ContentWrapper>
        <Accordion shouldHideAddButton onAdd={() => {}} addButtonText="">
          {assetsAccountDetailsContracts.map((assetAccountContract) => {
            const assetId = get(
              { postProductSelect: [assetAccountContract] },
              FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)
            );
            const assetIndex = getIndexFromId(assets, assetId);
            const asset = get(formValues, FIELD_PATHS.ASSET(assetIndex));
            const assetType: ASSET_TYPE = get({ assets: [asset] }, FIELD_PATHS.ASSET_TYPE(0));
            const assetTypeLabel = getFieldValueLabel(assetType);
            const assetOwners: Owner[] = get({ assets: [asset] }, FIELD_PATHS.ASSET_OWNERS(0), []);
            const ownerNames = getApplicantsFullnameFromOwners(assetOwners, applicants);
            const hasQuestion = getQuestionDisplayFields<ACCOUNT_BSB_QUESTIONS>(
              assetAccountContract.questions as ACCOUNT_BSB_QUESTIONS[]
            );
            return (
              <Accordion.Item
                key={assetId}
                header={<AccordionHeader header={assetTypeLabel} subHeader={ownerNames.join(' & ')} />}
              >
                <FunnelGrid direction="column">
                  <FunnelGridUnit mt="sm">
                    <FinancialInstitutionFunnelView assetIndex={assetIndex} isDisabled label="Institution" />
                  </FunnelGridUnit>
                  <FunnelGridUnit mt="sm">
                    <EstimatedValueFunnelView assetIndex={assetIndex} isDisabled label="Balance" />
                  </FunnelGridUnit>
                  {hasQuestion.ACCOUNT_BSB && (
                    <FunnelGridUnit mt="sm">
                      <AssetAccountBSBCustomerView assetIndex={assetIndex} />
                    </FunnelGridUnit>
                  )}
                  {hasQuestion.ACCOUNT_NUMBER && (
                    <FunnelGridUnit mt="sm">
                      <AssetAccountNumberCustomerView assetIndex={assetIndex} />
                    </FunnelGridUnit>
                  )}
                </FunnelGrid>
              </Accordion.Item>
            );
          })}
          {Object.entries(liabilityContracts).map(([liabilityId, contracts]) => {
            const liabilityIndex = getIndexFromId(liabilities, liabilityId);
            const liability = get(formValues, FIELD_PATHS.LIABILITY(liabilityIndex));
            const liabilityType: LIABILITY_TYPE = get({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_TYPE(0));
            const liabilityTypeLabel = getFieldValueLabel(liabilityType);
            const liabilityOwners: Owner[] = get({ liabilities: [liability] }, FIELD_PATHS.LIABILITY_OWNERS(0), []);
            const ownerNames = getApplicantsFullnameFromOwners(liabilityOwners, applicants);
            const { liabilityAccountContract, liabilityContract } = contracts;
            const hasQuestion = getQuestionDisplayFields<ACCOUNT_BSB_QUESTIONS>(
              (liabilityAccountContract?.questions as ACCOUNT_BSB_QUESTIONS[]) || []
            );
            const hasAnnualInterestRateQuestion = getQuestionDisplayFields<LIABILITY_QUESTIONS>(
              (liabilityContract?.questions as LIABILITY_QUESTIONS[]) || []
            );
            return (
              <Accordion.Item
                key={liabilityId}
                header={<AccordionHeader header={liabilityTypeLabel} subHeader={ownerNames.join(' & ')} />}
              >
                <FunnelGrid direction="column">
                  <FunnelGridUnit mt="sm">
                    <CurrentLenderFunnelView liabilityIndex={liabilityIndex} isDisabled label="Institution" />
                  </FunnelGridUnit>
                  <FunnelGridUnit mt="sm">
                    <LiabilityCreditLimitFunnelView liabilityIndex={liabilityIndex} isDisabled label="Credit limit" />
                  </FunnelGridUnit>
                  <FunnelGridUnit mt="sm">
                    <OutstandingBalanceFunnelView liabilityIndex={liabilityIndex} isDisabled label="Balance" />
                  </FunnelGridUnit>
                  {hasQuestion.ACCOUNT_BSB && (
                    <FunnelGridUnit mt="sm" size={1 / 2}>
                      <LiabilityAccountBSBCustomerView liabilityIndex={liabilityIndex} />
                    </FunnelGridUnit>
                  )}
                  {hasQuestion.ACCOUNT_NUMBER && (
                    <FunnelGridUnit mt="sm">
                      <LiabilityAccountNumberCustomerView liabilityIndex={liabilityIndex} />
                    </FunnelGridUnit>
                  )}
                  {hasAnnualInterestRateQuestion.ANNUAL_INTEREST_RATE && (
                    <FunnelGridUnit mt="sm">
                      <CurrentInterestRateFunnelView label="Interest rate" isOptional liabilityIndex={liabilityIndex} />
                    </FunnelGridUnit>
                  )}
                </FunnelGrid>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </ContentWrapper>
      <ActionButtonsWrapper>
        <NextArrowAction variant="primary" isFullWidth customText={isFromSummary ? 'Save' : 'Next'} />
      </ActionButtonsWrapper>
    </Container>
  );
};
